import { useState } from "react";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ApiError, orgKeys } from "@njbsoft/api-client";
import { validateRedirect } from "@njbsoft/utils";
import { useApiClient } from "@/lib/auth";
import { useAppStore } from "@/store";
import { switchToOrg } from "@/lib/org-switch";
import { tokenOrgId } from "@/lib/token-org";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

interface SelectOrgSearch {
  redirect?: string;
  /** `switch`: a settled session asked to change orgs (the sidebar's Switch
   *  Org), so the picker shows over an org the token still names. */
  intent?: "switch";
}

export const Route = createFileRoute("/select-org")({
  validateSearch: (search: Record<string, unknown>): SelectOrgSearch => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
    intent: search.intent === "switch" ? "switch" : undefined,
  }),
  beforeLoad: ({ search }) => {
    const state = useAppStore.getState();
    if (!state.isAuthenticated) {
      throw redirect({ to: "/login" });
    }
    // Only a remembered org the token itself names is a settled context, and
    // a settled context skips the picker unless the member asked to switch.
    // Any other remembered org is stale — a sign-in's token names no org — and
    // skipping the picker over it lands on a shell whose every org-scoped read
    // is refused.
    const settled = state.activeOrgId !== null && state.activeOrgId === tokenOrgId(state.token);
    if (settled && search.intent !== "switch") {
      throw redirect({ to: "/" });
    }
  },
  component: SelectOrgPage,
});

function SelectOrgPage() {
  const { t } = useTranslation(["org", "common"]);
  const navigate = useNavigate();
  const api = useApiClient();
  const { redirect: redirectParam } = Route.useSearch();
  const [switching, setSwitching] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: orgKeys.me(),
    queryFn: async () => {
      const { data, error, response } = await api.GET("/orgs/me");
      if (error) throw new ApiError(response.status, error.error ?? "Request failed");
      return data;
    },
  });

  const orgs = data?.orgs ?? [];
  const destination = validateRedirect(redirectParam) ?? "/";

  const handleSelectOrg = async (org: {
    id: string;
    name: string;
    slug: string;
    role: string;
    require_mfa?: boolean;
  }) => {
    setSwitching(org.id);
    try {
      await switchToOrg({
        orgId: org.id,
        orgName: org.name,
        orgRole: org.role,
        orgSlug: org.slug,
        requireMfa: org.require_mfa ?? false,
      });
      void navigate({ to: destination });
    } catch {
      setSwitching(null);
    }
  };

  const handlePersonal = async () => {
    setSwitching("personal");
    try {
      await switchToOrg({
        orgId: null,
        orgName: null,
        orgRole: null,
        orgSlug: null,
        requireMfa: false,
      });
      void navigate({ to: destination });
    } catch {
      setSwitching(null);
    }
  };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-surface">
      <div className="w-full max-w-md space-y-6 p-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-on-surface">{t("selectOrgTitle")}</h1>
          <p className="mt-2 text-sm text-muted">{t("selectOrgSubtitle")}</p>
        </div>

        {isLoading && (
          <div className="space-y-3">
            <p className="text-center text-sm text-muted">{t("loadingOrgs")}</p>
            <Skeleton variant="card" height={64} />
            <Skeleton variant="card" height={64} />
          </div>
        )}

        {isError && (
          <Card className="text-center">
            <p className="text-sm text-danger">{t("loadOrgsFailed")}</p>
            <button
              type="button"
              onClick={() => void refetch()}
              className="mt-3 rounded-md bg-primary px-4 py-2 text-sm text-on-primary hover:bg-primary-hover"
            >
              {t("retry")}
            </button>
          </Card>
        )}

        {!isLoading && !isError && (
          <div className="space-y-3">
            {orgs.map((org) => (
              <button
                key={org.id}
                type="button"
                disabled={switching !== null}
                onClick={() => void handleSelectOrg(org)}
                className="w-full rounded-lg border border-gray-200 bg-white p-4 text-left transition-colors hover:border-primary hover:bg-primary/5 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-primary dark:hover:bg-primary/10"
              >
                <p className="text-sm font-semibold text-on-surface">{org.name}</p>
                <p className="text-xs text-muted">{org.role}</p>
              </button>
            ))}

            {/* Divider */}
            <div className="flex items-center gap-3 py-1">
              <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
            </div>

            {/* Personal option */}
            <button
              type="button"
              disabled={switching !== null}
              onClick={() => void handlePersonal()}
              className="w-full rounded-lg border border-gray-200 bg-white p-4 text-left transition-colors hover:border-primary hover:bg-primary/5 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-primary dark:hover:bg-primary/10"
            >
              <p className="text-sm font-semibold text-on-surface">{t("continueAsPersonal")}</p>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
